import { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import dayjs from "dayjs";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import MonthSelector from "components/MonthSelector";
import useCommon from "hooks/useCommon";
import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import { resetBudgetState } from "store/slice/maintenanceSlice";

const MaintenanceBudgetDialog = ({
  open,
  onClose,
  currentBudget = 0,
  loading = false,
  error = null,
  onSave,
}) => {
  const { black, primary, light, dark } = colors;

  const { DISPATCH, STORE } = useCommon();

  const [budget, setBudget] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (open) {
      DISPATCH(resetBudgetState());
      setBudget(currentBudget || "");
      setSelectedMonth(dayjs().format("YYYY-MM"));
      setValidationError("");
    }
  }, [open, currentBudget]);

  const handleSubmit = () => {
    if (loading) return;

    if (!budget || Number(budget) <= 0) {
      setValidationError("Monthly budget must be greater than 0");
      return;
    }

    onSave?.({
      budget: Number(budget),
      month: selectedMonth,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (loading) return;
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ px: 2 }}>
        <VuiTypography variant="h5">Set Monthly Budget</VuiTypography>
      </DialogTitle>

      <DialogContent sx={{ px: 1 }}>
        <VuiBox
          mb={3}
          px={2}
          py={2}
          borderRadius="xl"
          sx={{
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${rgba(dark.main, 0.25)}`,
          }}
        >
          <VuiTypography variant="caption" textTransform="uppercase" letterSpacing="1px" mb={1}>
            Budget Month
          </VuiTypography>

          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <VuiTypography variant="h5" fontWeight="medium">
              {dayjs(selectedMonth).format("MMMM YYYY")}
            </VuiTypography>

            <MonthSelector
              value={selectedMonth}
              onChange={setSelectedMonth}
              label="Select Budget Month"
              width={180}
              clearable={false}
              disabled={loading}
            />
          </VuiBox>
        </VuiBox>

        <VuiBox>
          <VuiTypography variant="caption" ml={1} mb={0.5} display="block">
            Monthly Maintenance Budget *
          </VuiTypography>

          <VuiInput
            gradient
            fullWidth
            type="number"
            placeholder="Enter maintenance budget"
            value={budget}
            disabled={loading}
            inputProps={{
              min: 0,
            }}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "" || Number(value) >= 0) {
                setBudget(value);
                setValidationError("");
              }
            }}
          />

          {validationError && (
            <VuiTypography variant="caption" color="error" mt={0.5} display="block">
              {validationError}
            </VuiTypography>
          )}

          {error && (
            <VuiTypography variant="caption" color="error" mt={1} display="block">
              {typeof error === "string" ? error : error?.message || "Failed to update budget"}
            </VuiTypography>
          )}
        </VuiBox>
      </DialogContent>

      <DialogActions>
        <VuiButton variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </VuiButton>

        <VuiButton
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            minWidth: 140,
          }}
        >
          {loading ? (
            <VuiBox display="flex" alignItems="center" justifyContent="center" gap={1}>
              <CircularProgress color="light" size={16} thickness={5} />
            </VuiBox>
          ) : (
            "Save Budget"
          )}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceBudgetDialog;
